import { useEffect, useRef, useState } from "react";
import { MessageSquare, Send, Loader2, X, Paperclip, FileText, ThumbsUp, ThumbsDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { papi, errText } from "./portalApi";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const TOPICS = ["Not live yet (no scan)", "Stuck, no movement for days", "Delivered but customer says not received", "Returned / held at customs", "Other"];

// After checking Parcelforce / tracking: tell the team something is wrong with this parcel's tracking.
export const TrackIssueButton = ({ g, onOpen }) => (g.tracking && !["cancelled", "deleted"].includes(g.status) ? (
  <button onClick={() => onOpen(g)} title="Tracking not live or a delivery problem? Tell the team" data-testid={`portal-track-issue-${g.key}`}
    className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-rose-700"><AlertTriangle className="h-3 w-3" /> Tracking issue</button>) : null);

const hoverCache = {};
const W = 380;
/* Hover chat: fixed to the screen (never cut off by the page edge), keeps the whole thread readable and lets the partner
   reply right there. Partner messages blue, office replies green. */
const QueryPeek = ({ g, rect, onEnter, onLeave }) => {
  const [d, setD] = useState(hoverCache[g.key]?.d || null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = (force) => {
    const c = hoverCache[g.key];
    if (!force && c && Date.now() - c.at < 30000) return;
    papi("get", `/parcels/${encodeURIComponent(g.key)}/query`)
      .then((r) => { hoverCache[g.key] = { at: Date.now(), d: r }; setD(r); }).catch(() => setD({ current: null }));
  };
  useEffect(() => { load(false); }, [g.key]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await papi("post", `/parcels/${encodeURIComponent(g.key)}/query`, { text });
      const nd = { current: r.current }; hoverCache[g.key] = { at: Date.now(), d: nd }; setD(nd); setText("");
      toast.success("Reply sent"); window.dispatchEvent(new Event("portal-queries-changed"));
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const cur = d?.current;
  const left = Math.max(8, Math.min(rect.right - W, window.innerWidth - W - 8));
  const below = window.innerHeight - rect.bottom > 360;
  const pos = below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 };
  return (
    <div style={{ position: "fixed", left, width: W, ...pos }} onMouseEnter={onEnter} onMouseLeave={onLeave}
      className="z-50 rounded-xl border bg-white p-2.5 text-left text-xs shadow-2xl" data-testid={`portal-query-peek-${g.key}`}>
      <div className="mb-1.5 flex items-center gap-2 text-[10px] font-semibold text-slate-500">
        <span className="truncate">{g.customer_name}</span>
        <span className="ml-auto inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-blue-500" />You</span>
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-full bg-emerald-500" />Office</span></div>
      {!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {!cur && <p className="text-slate-400">No queries yet — write one below.</p>}
          {cur?.entries.map((e, i) => <Bubble key={i} e={e} />)}
          {cur?.status === "solved" && <p className="font-semibold text-emerald-700">Solved{cur.outcome ? `: ${cur.outcome}` : ""}</p>}
          {cur && cur.status !== "solved" && cur.awaiting === "team" && <p className="text-[10px] text-amber-700">Waiting for the office to reply</p>}
        </div>)}
      <div className="mt-2 flex items-end gap-1.5">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={cur && cur.status !== "solved" ? "Reply…" : "Write a query…"}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="min-w-0 flex-1 resize-none rounded-lg border border-blue-200 p-1.5 text-xs focus:outline-blue-400" data-testid={`portal-peek-reply-${g.key}`} />
        <button onClick={send} disabled={busy || !text.trim()} className="rounded-lg bg-blue-600 p-2 text-white disabled:opacity-40" data-testid={`portal-peek-send-${g.key}`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
      </div>
    </div>
  );
};

const Bubble = ({ e, full }) => {
  const me = e.from === "partner";
  return (
    <div className={`rounded-lg border px-2 py-1 ${me ? "ml-8 border-blue-200 bg-blue-50" : "mr-8 border-emerald-200 bg-emerald-50"}`}>
      <div className={`text-[10px] font-semibold ${me ? "text-blue-700" : "text-emerald-700"}`}>{me ? "You" : "Office"} <span className="font-normal text-slate-500">· {when(e.at)}</span></div>
      <div className="whitespace-pre-wrap break-words text-[11px] text-slate-800">{e.text}</div>
      {e.files?.length > 0 && (full ? <div className="mt-1 flex flex-wrap gap-1">{e.files.map((f) => <FileLink key={f.id} f={f} />)}</div>
        : <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 text-[10px] text-slate-600"><Paperclip className="h-3 w-3" /> {e.files.length} attachment(s)</div>)}
    </div>
  );
};

// Blinks while the team has replied and the query is not solved yet
export const QueryButton = ({ g, onOpen }) => {
  const q = g.query;
  const [rect, setRect] = useState(null);
  const tm = useRef(null);
  const blink = q && q.status !== "solved" && q.state !== "solved" && q.state !== "processed" && q.awaiting === "partner";
  const stay = () => clearTimeout(tm.current);
  const leave = () => { tm.current = setTimeout(() => setRect(null), 300); };
  useEffect(() => {
    if (!rect) return undefined;
    const off = () => setRect(null);
    window.addEventListener("scroll", off, true);
    return () => window.removeEventListener("scroll", off, true);
  }, [rect]);
  return (
    <span className="relative inline-block" onMouseEnter={(e) => { stay(); if (q) setRect(e.currentTarget.getBoundingClientRect()); }} onMouseLeave={leave}>
    <button onClick={() => { setRect(null); onOpen(g); }} className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${blink ? "animate-pulse border-fuchsia-400 bg-fuchsia-50 text-fuchsia-700" : "text-slate-600"}`}
      data-testid={`portal-query-btn-${g.key}`}>
      <MessageSquare className="h-3 w-3" /> {blink ? "Reply received" : "Query"}
    </button>
    {rect && <QueryPeek g={g} rect={rect} onEnter={stay} onLeave={leave} />}
    </span>
  );
};

const fileCache = {};
const FileLink = ({ f }) => {
  const [url, setUrl] = useState(fileCache[f.id] || "");
  const img = f.content_type?.startsWith("image/");
  const get = async () => {
    if (!fileCache[f.id]) fileCache[f.id] = URL.createObjectURL(await papi("get", `/query-files/${f.id}`, null, { responseType: "blob" }));
    return fileCache[f.id];
  };
  useEffect(() => { if (img && !url) get().then(setUrl).catch(() => {}); }, [f.id]);
  const open = async () => { try { window.open(await get(), "_blank"); } catch (e) { toast.error(errText(e)); } };
  return img ? <button onClick={open} title={f.filename}>{url ? <img src={url} alt={f.filename} className="h-16 w-16 rounded border object-cover" /> : <span className="text-[11px]">{f.filename}</span>}</button>
    : <button onClick={open} className="inline-flex items-center gap-1 rounded border bg-white px-2 py-0.5 text-[11px] text-teal-700"><FileText className="h-3 w-3" />{f.filename}</button>;
};

const RateAnswer = ({ cur, onRated }) => {
  const [down, setDown] = useState(false);
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  if (!cur.rating && !cur.can_rate) return null;
  if (cur.rating) return (
    <p className={`text-[11px] font-semibold ${cur.rating.value === "up" ? "text-emerald-700" : "text-rose-700"}`} data-testid="portal-query-rated">
      {cur.rating.value === "up" ? "You rated this answer helpful. Thank you!" : "You said this wasn't solved — the team is looking again."}</p>);
  const rate = async (rating) => {
    setBusy(true);
    try { const r = await papi("post", `/queries/${cur.id}/rate`, { rating, reason: why }); toast.success(rating === "up" ? "Thanks for the feedback" : "Query reopened for the team"); onRated(r.current); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2" data-testid="portal-query-rate">
      <div className="flex items-center gap-2 text-xs"><span className="font-semibold text-slate-700">Was this answer helpful?</span>
        <button onClick={() => rate("up")} disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-emerald-400 bg-white px-2 py-1 text-emerald-700 hover:bg-emerald-100" data-testid="portal-query-rate-up"><ThumbsUp className="h-3.5 w-3.5" /> Yes</button>
        <button onClick={() => setDown(!down)} disabled={busy} className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 ${down ? "border-rose-500 bg-rose-500 text-white" : "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"}`} data-testid="portal-query-rate-down"><ThumbsDown className="h-3.5 w-3.5" /> No</button></div>
      {down && <div className="mt-2 flex gap-2">
        <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="What is still wrong?" maxLength={500} className="h-8 flex-1 rounded-lg border px-2 text-xs" data-testid="portal-query-rate-reason" />
        <button onClick={() => rate("down")} disabled={busy || !why.trim()} className="rounded-lg bg-rose-600 px-3 text-xs font-semibold text-white disabled:opacity-50" data-testid="portal-query-rate-send">Send & reopen</button></div>}
    </div>
  );
};

export const QueryDialog = ({ g, onClose, onSent, concern = false }) => {
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState([]);
  useEffect(() => { papi("get", `/parcels/${encodeURIComponent(g.key)}/query`).then(setD).catch((e) => { toast.error(errText(e)); setD({ current: null }); }); }, [g.key]);
  const canSend = concern ? topic && (topic !== "Other" || text.trim()) : text.trim() || files.length;
  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      let r;
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        if (text.trim()) fd.append("text", text.trim());
        if (concern) fd.append("topic", topic);
        r = await papi("post", `/parcels/${encodeURIComponent(g.key)}/query/files`, fd);
      } else r = await papi("post", `/parcels/${encodeURIComponent(g.key)}/query`, { text, ...(concern ? { topic } : {}) });
      setD({ current: r.current }); setText(""); setFiles([]); setTopic(""); toast.success(concern ? "Tracking concern sent to the team" : "Query sent"); onSent?.();
      window.dispatchEvent(new Event("portal-queries-changed"));
    }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const cur = d?.current;
  const solve = async () => {
    if (!window.confirm("Mark this query as solved? You can write again any time to reopen it.")) return;
    setBusy(true);
    try {
      await papi("post", `/parcels/${encodeURIComponent(g.key)}/query/solve`, {});
      const r = await papi("get", `/parcels/${encodeURIComponent(g.key)}/query`);
      setD(r); toast.success("Query closed — thank you!"); onSent?.();
      window.dispatchEvent(new Event("portal-queries-changed"));
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" data-testid="portal-query-dialog">
      <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-xl">
        <div className="mb-2 flex items-center gap-2"><b className="text-sm">{concern ? "Tracking concern" : "Customer query"}: {g.customer_name}</b>
          {concern && g.tracking && <span className="font-mono text-xs text-slate-500" data-testid="portal-concern-tracking">{g.tracking}</span>}
          <button onClick={onClose} className="ml-auto" data-testid="portal-query-close"><X className="h-4 w-4" /></button></div>
        {concern && (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="portal-concern-topics">
            {TOPICS.map((t, i) => (
              <button key={t} onClick={() => setTopic(t)} data-testid={`portal-concern-topic-${i}`}
                className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${topic === t ? "border-rose-600 bg-rose-600 text-white" : "border-rose-200 bg-rose-50 text-rose-800"}`}>{t}</button>))}
          </div>)}
        {!d ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <div className="mb-2 max-h-72 space-y-1.5 overflow-auto" data-testid="portal-query-thread">
            {!cur && <p className="text-xs text-slate-400">No queries yet for this order.</p>}
            {cur?.entries.map((e, i) => <Bubble key={i} e={e} full />)}
            {cur && (cur.status === "solved" ? <><p className="text-xs font-semibold text-emerald-700">Solved{cur.outcome ? `: ${cur.outcome}` : ""}</p>
              <RateAnswer cur={cur} onRated={(c) => { setD({ current: c }); onSent?.(); }} /></>
              : <>{cur.awaiting === "team" ? <p className="text-[11px] text-amber-700">Waiting for the team to reply</p> : null}
                {cur.entries.some((e) => e.kind !== "partner") && (
                  <button onClick={solve} disabled={busy} className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-emerald-500 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
                    title="Happy with the answer? Close this query" data-testid="portal-query-solve"><CheckCircle2 className="h-4 w-4" /> Solved — I'm satisfied</button>)}</>)}
          </div>)}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={concern ? (topic === "Other" ? "Describe the concern…" : "Anything to add? (optional)") : cur && cur.status !== "solved" ? "Add a message…" : cur?.partner_solved ? "Write again to reopen this query…" : "Write your query…"}
          className="w-full rounded-lg border p-2 text-sm" data-testid="portal-query-text" />
        <div className="mt-2 flex items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border px-2 py-1.5 text-xs text-slate-600" title="Photos or PDF, max 10 MB each, up to 5" data-testid="portal-query-attach">
            <Paperclip className="h-3.5 w-3.5" /> Attach
            <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="portal-query-attach-input"
              onChange={(e) => { setFiles([...e.target.files].slice(0, 5)); e.target.value = ""; }} /></label>
          {files.length > 0 && <span className="truncate text-[11px] text-slate-500" data-testid="portal-query-files">{files.map((f) => f.name).join(", ")}</span>}
          <button onClick={send} disabled={busy || !canSend} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-teal-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" data-testid="portal-query-send">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {concern ? "Send concern" : "Send query"}</button></div>
      </div>
    </div>
  );
};
